import React from 'react'
import { Box, Typography, Button, Stack, keyframes } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import FolderIcon from '@mui/icons-material/Folder'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`

type Props = {
  onUploadClick: () => void
  /** Se está dentro de uma pasta (muda a mensagem) */
  insideFolder?: boolean
  /** Nome da pasta atual */
  folderName?: string
  /** Callback para criar sub-pasta */
  onCreateFolder?: () => void
}

export default function EmptyState({ onUploadClick, insideFolder, folderName, onCreateFolder }: Props) {
  // Estado vazio dentro de uma pasta
  if (insideFolder) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 3,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            mb: 4,
            animation: `${float} 3s ease-in-out infinite`
          }}
        >
          <FolderIcon sx={{ fontSize: 100, color: '#e0e0e0' }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
          Pasta vazia
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 3, maxWidth: 400 }}>
          {folderName ? `A pasta "${folderName}" está vazia. ` : ''}
          Arraste arquivos para cá ou faça upload para adicionar conteúdo.
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={onUploadClick}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: 16,
              boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(25, 118, 210, 0.45)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Enviar arquivo
          </Button>
          {onCreateFolder && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<CreateNewFolderIcon />}
              onClick={onCreateFolder}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: 16
              }}
            >
              Nova pasta
            </Button>
          )}
        </Stack>
      </Box>
    )
  }

  // Estado vazio na raiz (original)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 3,
        textAlign: 'center'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          mb: 4,
          animation: `${float} 3s ease-in-out infinite`
        }}
      >
        <CloudUploadIcon
          sx={{
            fontSize: 100,
            color: '#e0e0e0'
          }}
        />
        <AudioFileIcon
          sx={{
            fontSize: 32,
            color: '#1976d2',
            position: 'absolute',
            bottom: -5,
            left: -10,
            transform: 'rotate(-15deg)'
          }}
        />
        <VideoFileIcon
          sx={{
            fontSize: 32,
            color: '#9c27b0',
            position: 'absolute',
            bottom: -5,
            right: -10,
            transform: 'rotate(15deg)'
          }}
        />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
        Nenhum arquivo ainda
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 3, maxWidth: 400 }}>
        Arraste e solte arquivos de áudio ou vídeo aqui, ou clique no botão abaixo para selecionar
      </Typography>

      <Button
        variant="contained"
        size="large"
        startIcon={<CloudUploadIcon />}
        onClick={onUploadClick}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 3,
          textTransform: 'none',
          fontSize: 16,
          boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.45)',
            transform: 'translateY(-2px)'
          },
          transition: 'all 0.2s ease'
        }}
      >
        Enviar arquivo
      </Button>

      <Typography variant="caption" sx={{ color: '#999', mt: 2 }}>
        Formatos suportados: MP3, WAV, MP4, MOV, AVI, MKV e outros
      </Typography>
    </Box>
  )
}
